"use client";

import { useGetTransactionsQuery, useLazyGetInvoiceQuery } from "@/services/paymentApi";
import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ReceiptText,
  CheckCircle2,
  X,
  CreditCard,
  Loader2,
  Download,
} from "lucide-react";

export default function TransactionHistoryCard({
  refreshSignal = 0,
  isExternalLoading = false,
}: {
  refreshSignal?: number;
  isExternalLoading?: boolean;
}) {
  const { data, isLoading, refetch } = useGetTransactionsQuery(undefined, { refetchOnMountOrArgChange: true });
  const [triggerInvoice] = useLazyGetInvoiceQuery();
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<string | null>(null);

  useEffect(() => {
    if (refreshSignal > 0) {
      refetch();
    }
  }, [refreshSignal, refetch]);

  const handleDownloadInvoice = async (transactionId: string) => {
    try {
      setLoadingInvoiceId(transactionId);
      const response = await triggerInvoice(transactionId).unwrap();
      if (response?.success && response?.invoice_url) {
        window.open(response.invoice_url, "_blank");
      }
    } catch (e) {
      console.error("Failed to download invoice", e);
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  const transactions = Array.isArray(data?.transactions) ? data.transactions : [];
  const latestTransaction = transactions.length > 0 ? transactions[0] : null;

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(dateString));
  };

  const getCurrency = (currencyObj: any) => {
    return currencyObj ? Object.keys(currencyObj)[0] || "USD" : "USD";
  };

  const formatAmount = (amount: number | string | undefined, currencyObj: any) => {
    if (amount === undefined || amount === null) return "N/A";
    const currency = getCurrency(currencyObj);
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(numAmount);
  };

  if (isLoading || isExternalLoading) {
    return (
      <div className="flex h-[180px] flex-col justify-center items-center rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.01))] shadow-[0_20px_70px_rgba(0,0,0,0.28)]">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex h-full min-h-0 flex-col rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.28)]">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 border border-white/10">
            <ReceiptText className="h-5 w-5 text-zinc-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Transaction History</h2>
            <p className="text-xs text-zinc-400">No transactions recorded yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog.Root>
      <div className="flex h-full min-h-0 flex-col justify-between rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.28)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 border border-white/10">
              <ReceiptText className="h-5 w-5 text-zinc-300" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">Transaction History</h2>
              <p className="text-xs text-zinc-400">
                {transactions.length} transaction{transactions.length > 1 ? "s" : ""}.
              </p>
            </div>
          </div>
          <Dialog.Trigger asChild>
            <button className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#ac274f] to-[#eb638b] shadow-[0_18px_40px_rgba(172,39,79,0.32)] px-3 py-1.5 text-[11px] font-medium text-white transition hover:scale-[1.02] cursor-pointer">
              View All
            </button>
          </Dialog.Trigger>
        </div>

        {latestTransaction && (
          <div className="mt-4 rounded-2xl border border-white/5 bg-black/20 p-3">
            <div className="flex justify-between items-center mb-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                Latest Transaction
              </p>
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.2)] shrink-0">
                <CheckCircle2 className="h-3 w-3 text-emerald-400 shrink-0" />
                <span className="text-[10px] whitespace-nowrap font-medium text-emerald-400 uppercase tracking-wider">
                  {latestTransaction.status}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-zinc-300">
                  Invoice <span className="text-white font-medium">#{latestTransaction.invoice_number}</span>
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <p className="text-[11px] text-zinc-500">
                    {formatDate(latestTransaction.created_at)}
                  </p>
                  <span className="text-zinc-700 text-[10px] hidden sm:inline">•</span>
                  <p className="text-[10px] text-zinc-500 font-mono truncate max-w-[150px]" title={latestTransaction.transaction_id}>
                    {latestTransaction.transaction_id}
                  </p>
                </div>
              </div>
              <div className="text-right flex flex-col items-end gap-1">
                <p className="text-base font-semibold text-white">
                  {formatAmount(latestTransaction.amounts?.amount_paid, latestTransaction.currency_code)}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] whitespace-nowrap font-medium px-2 py-0.5 rounded-full bg-white/10 text-zinc-300">
                    {latestTransaction.tier_subscribed} Tier
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-[95vw] h-[95vh] max-w-none translate-x-[-50%] translate-y-[-50%] p-4 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] flex flex-col">
          <div className="flex flex-col flex-1 h-full rounded-[28px] border border-white/10 bg-[#0c0c0c] shadow-[0_24px_100px_rgba(0,0,0,0.8)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 p-6 bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-zinc-800 to-zinc-900 border border-white/10 shadow-inner">
                  <ReceiptText className="h-5 w-5 text-zinc-300" />
                </div>
                <div>
                  <Dialog.Title className="text-lg font-semibold text-white">
                    Transaction History
                  </Dialog.Title>
                  <Dialog.Description className="text-xs text-zinc-400 mt-0.5">
                    Your full billing history and invoices.
                  </Dialog.Description>
                </div>
              </div>
              <Dialog.Close className="rounded-full p-2 hover:bg-white/10 transition text-zinc-400 hover:text-white cursor-pointer">
                <X className="h-5 w-5" />
              </Dialog.Close>
            </div>

            {/* Content List */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3 custom-scrollbar">
              {transactions.map((tx: any) => (
                <div
                  key={tx.transaction_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl border border-white/5 bg-white/[0.01] hover:bg-white/[0.03] transition group"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-white/5 border border-white/5 text-zinc-400 group-hover:text-white transition">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white mb-0.5">
                        Invoice #{tx.invoice_number}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-xs text-zinc-500">
                          {formatDate(tx.created_at)}
                        </p>
                        <span className="text-zinc-700 text-[10px] hidden sm:inline">•</span>
                        <p className="text-[11px] text-zinc-500 font-mono" title={tx.transaction_id}>
                          {tx.transaction_id}
                        </p>
                      </div>

                      {/* Mobile minimal sub-status */}
                      <div className="flex items-center gap-2 mt-2 sm:hidden shrink-0">
                        <span className={`text-[10px] whitespace-nowrap font-medium uppercase tracking-wider px-2 py-0.5 rounded-md ${tx.status === "completed"
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "bg-zinc-500/10 text-zinc-400 border border-zinc-500/20"
                          }`}>
                          {tx.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-6 border-t border-white/5 sm:border-t-0 pt-3 sm:pt-0">
                    <div className="hidden sm:block shrink-0">
                      <span className={`text-[10px] whitespace-nowrap font-medium uppercase tracking-wider px-2 py-1 rounded-md ${tx.status === "completed"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : "bg-zinc-500/10 text-zinc-400 border border-zinc-500/20"
                        }`}>
                        {tx.status}
                      </span>
                    </div>

                    <div className="text-left sm:text-right sm:w-[110px] shrink-0">
                      <p className="text-sm font-semibold text-white flex items-center sm:justify-end gap-1">
                        {formatAmount(tx.amounts?.amount_paid, tx.currency_code)}
                      </p>
                      <div className="flex items-center sm:justify-end gap-2 mt-1 shrink-0">
                        <span className="text-[10px] whitespace-nowrap font-medium px-2 py-0.5 rounded-full bg-white/10 text-zinc-300 border border-white/5">
                          {tx.tier_subscribed} Tier
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDownloadInvoice(tx.transaction_id)}
                      disabled={loadingInvoiceId === tx.transaction_id}
                      className="flex items-center justify-center h-8 w-8 rounded-full border border-white/10 bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download Invoice"
                    >
                      {loadingInvoiceId === tx.transaction_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 bg-white/[0.02]">
              <p className="text-center text-xs text-zinc-600">
                If you have questions about a transaction, please contact support.
              </p>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
